import { mapState, mapGetters } from 'vuex'
import { storage } from '@shared/utils/storage'
import Loadable from '@shared/mixins/loadable'
import Translatable from '@shared/mixins/translatable'
import { FormMixin } from '@shared/mixins/forms'

import ReactionSlider from './reaction-slider'
import StageCell from './stage-cell'

function offset(some, by=2) {
  return `000000000000${some}`.substr(0-by, by)
}

const format = d => `${d.getFullYear()}-${offset(d.getMonth() + 1)}-${offset(d.getDate())}`


const Sync = {
  inject: ['stagesService'],

  data() { return {
  } },

  methods: {
    start(texts={}) {
      this.$refs.loader.open()

      this.stagesService.sync()
        .then(data => {
          this.$refs.success.open()
        })
        .catch(error => {
          if (error.response.data.status == 304) {
            this.$refs.success.open()
          } else {
            this.$refs.auth.open()
          }
        })
        .then(x => this.$refs.loader.close())
    }
  }
}


const Share = {
  mixins: [Loadable, FormMixin],
  inject: ['stagesService'],

  data() { return {
    response: {}
  } },

  created() {
    this.$on('submit-success', x => {
      this.$refs.email.close()
      this.$refs.success.open()
    })
  },

  methods: {
    clean() {
      this.response = {}
    },

    viaEmail() {
      this.$refs.email.open()
    },

    start(method='list', params) {
      this.clean()
      this.$refs.loader.open()

      this.stagesService[method](params)
        .then(data => {
          this.response = data
          this.$refs.loader.close()
          this.$refs[method].open()
        })
    },
  }
}


export default {
  template: '#calendar-stage',
  mixins: [Translatable],
  inject: ['productsService', 'stagesService'],

  components: {
    ReactionSlider,
    StageCell,
    Sync,
    Share,
  },

  data() {
    let today = new Date()
    today.setHours(0,0,1,0)

    return {
      prevOrder: void(0),
      stage: {
        days_periods: [],
        user_stage_products: [],
      },

      stageDays: [],
      cols: [],
      offset: 0,
      dates: [],
      today: today,

      nutrilon: 1,
      pickADate: false
    }
  },

  watch: {
    stage(nval, oval) {
      this.nutrilon = this.user.nutrilon || storage.getItem('#nutrilon') || this.nutrilon
      this.prevOrder = oval.order
      this.stageDays = this.generateDays()
    },

    isAuthenticated(nval, oval) {
      if (nval == true && oval == false) {
        this.$nextTick(x => this.initStaff())
      }
    },

    stageDays() {
      this.cols = this.generateCols()

      let dates = this.stageDays.map(x => x.date)
      dates.length -= Math.min(dates.length, 7) - 1

      if (this.prevOrder < this.stage.order) {
        this.offset = 0
      } else if (this.prevOrder > this.stage.order && this.stage.is_history) {
        this.offset = dates.length - 1
      } else {
        let offset = this.offset ? this.offset : dates.findIndex(date => this.isToday(date))
        this.offset = Math.max(
          offset < dates.length ? offset : dates.length - 1, 0
        )
      }

      this.dates = dates
    },

    offset() {
      this.cols = this.generateCols()
    }
  },

  created() {
    this.stageDays = this.generateDays()
    this.initStaff()
  },

  computed: {
    ...mapState('user', ['user']),
    ...mapGetters('user', ['isAuthenticated']),

    hasPreviousStage() {
      let stages = this.getSwitchableStages().filter(x => x.order < this.stage.order)
      return this.stage.order > 1 && stages.length > 0
    },
    hasPrevious() { return this.offset > 0 },
    hasNext() { return this.offset < this.stageDays.length - 7 },
    reactions() { return this.productsService.reactions },
  },

  methods: {
    initStaff() {
      this.nutrilon = this.user.nutrilon || storage.getItem('#nutrilon') || this.nutrilon
      this.productsService.updateReactions()
    },

    generateCols() {
      let today = new Date(this.today)
      let cols = this.stageDays.slice(this.offset, this.offset + 7)

      if (cols.length > 0) today = cols[cols.length - 1].date
      for (let i = cols.length, l = i; i < 7; i++)
        cols.push({
          date: today.addDays(i - l + 1),
          rows: []
        })

      return cols
    },

    generateDays() {
      let periods = this.stage.days_periods
        .map(x => {
          x.date = new Date(x.date)
          x.date.setHours(0,0,1,0)

          return x
        })
        .sort((a, b) => {
          let date = a.date - b.date

          return date !== 0 ? date : parseInt(a.feed_period.starts_at) - parseInt(b.feed_period.starts_at)
        })

      let days = []
      let date = null

      periods.forEach(period => {
        if (date === null || period.date.getTime() != date.getTime()) {
          date = period.date
          days.push({ date: period.date, rows: [] })
        }

        days[days.length - 1].rows.push(period)
      })

      return days
    },

    range() {
      let rows = []
      for (let i = 0; i < 5; i++) rows.push(i)

      return rows
    },

    day(date) {
      return `${offset(date.getDate())}.${offset(date.getMonth() + 1)}`
    },

    isToday(date) {
      if (!this.today || !date || this.stage.is_history) return false

      if (this.stageDays.length > 0) {
        let first = this.stageDays[0].date
        if (first > this.today) return this.day(first) == this.day(date)

        let last = this.stageDays.find(x => x.date.getTime() >= this.today.getTime())
        if (last != null) return this.day(last.date) == this.day(date)
      }

      return this.day(this.today) == this.day(date)
    },

    isHistory(date) {
      if (this.stage.is_history) return true
      // let today = this.stageDays.find(x => this.isToday(x.date))
      // if (typeof today === 'undefined') return false

      return date.getTime() < this.today.getTime()
    },

    next() {
      this.offset++
    },

    getSwitchableStages() {
      return this.stagesService.getSwitchableStages()
    },

    previousStage() {
      let stages = this.getSwitchableStages().filter(x => x.order < this.stage.order)
      this.$emit('switch-stage', stages[stages.length - 1].order)
    },

    nextStage() {
      let stages = this.getSwitchableStages().filter(x => x.order > this.stage.order)
      this.$emit('switch-stage', stages[0].order)
    },

    newStage() {
      this.$refs.newStage.close()
      this.$emit('new-stage')
    },

    previous() {
      this.offset--
    },

    toggleDatepicker(date) {
      let today = this.isToday(date)
      this.pickADate = today ^ this.pickADate

      if (today && this.pickADate) {
        setTimeout(x => $(document).on('click.picker-close', e => {
          this.pickADate = false
          $(document).off('click.picker-close')
        }), 300)
      } else {
        $(document).off('click.picker-close')
      }
    },

    shift(date_from, date_to) {
      this.pickADate = false
      date_from.setHours(0,0,1,0)
      date_to.setHours(0,0,1,0)

      if(date_from.getTime() == date_to.getTime()) return

      this.$refs.loader.open()

      this.stagesService.shiftStage(
        this.stage.order, format(date_from), format(date_to)
      )
        .then(stage => {
          this.$parent.stageReady(stage)
          this.$refs.loader.close()
        })
    },

    removeReaction({ type, item }) {
      let method = {
        liked: 'like',
        disliked: 'dislike',
        allergic: 'allergy',
      }[type]

      this.productsService[method](item.product.id).then(stage => this.$parent.stageReady(stage))
    },

    sync() {
      this.$refs.sync.start()
    },

    shareList() {
      this.$refs.share.start('list', {
        stage: this.stage.order,
        date_from: format(this.cols[0].date),
        date_to: format(this.cols[this.cols.length - 1].date),
      })
    },

    shareTable() {
      this.$refs.share.start('table', {
        stage: this.stage.order,
        date_from: format(this.cols[0].date),
        date_to: format(this.cols[this.cols.length - 1].date),
      })
    },

    expand() {
      this.$refs.loader.open()
      this.stagesService.stages_list = {}
      this.stagesService.getStages()
        .then(data => {
          this.$refs.loader.close()
          let stage = data.find(x => x.order == 5)
          let chooser = this.$parent.$refs.selector.$refs.chooser

          if (stage.products.length == 0) {
            this.$parent.finish()
          } else {
            chooser.start([5], {
              title: this.i18n.expandFifthStageTitle,
              description: this.i18n.expandFifthStageDescription
            })
              .then(data => {
                this.$refs.loader.open()
                this.stagesService.initializeStage(
                  { stage: data[0], expand: true }
                )
                  .then(stage => {
                    this.$refs.loader.close()
                    this.$parent.stageReady(stage)
                  })
              })
          }
        })
    }
  }
}



// WEBPACK FOOTER //
// ./src/js/calendar/components/stage.js